import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Alert,
  Snackbar,
  Divider,
  TextField,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { getProduct, getSKUs } from '../app/api';
import { useTranslation } from '../app/LanguageContext';
import { formatCurrency } from '../app/currency';
import MediaGallery from '../components/MediaGallery';

const ProductViewPage = () => {
  const { translate } = useTranslation();
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [skus, setSkus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate();

  const loadData = async () => {
    try {
      setLoading(true);
      const productResponse = await getProduct(id);
      setProduct(productResponse.data);
      if (productResponse.data?.id) {
        const skusResponse = await getSKUs(productResponse.data.id);
        setSkus(skusResponse.data || []);
      } else {
        setSkus([]);
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [id]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE':
        return 'success';
      case 'INACTIVE':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'ACTIVE':
        return translate('products.active');
      case 'INACTIVE':
        return translate('products.inactive');
      default:
        return status;
    }
  };

  const getTypeLabel = (type) => {
    if (type === 'SERVICE') return translate('products.item_type_service');
    return translate('products.item_type_product');
  };

  const formatPrice = (price, priceType = 'FIXED') => {
    const formatted = formatCurrency(price);
    if (priceType === 'FROM_STARTING') {
      return `${translate('products.price_from_prefix')} ${formatted}`;
    }
    return formatted;
  };

  const formatAttributes = (attributes) => {
    if (!attributes || Object.keys(attributes).length === 0) {
      return '-';
    }
    return Object.entries(attributes)
      .map(([key, value]) => `${key}: ${value}`)
      .join(', ');
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          {product ? getTypeLabel(product.type) : translate('products.item_type_product')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={() => navigate(`/products/${product?.code || id}/edit`)}
        >
          {translate('common.edit')}
        </Button>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Box sx={{ flex: { xs: '1', sm: '0.5' } }}>
            <TextField
              fullWidth
              label={translate('products.name')}
              value={product?.name || ''}
              InputProps={{
                readOnly: true,
              }}
              sx={{
                '& .MuiInputBase-root': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            />
          </Box>
          <Box sx={{ flex: { xs: '1', sm: '0.2' } }}>
            <TextField
              fullWidth
              label={translate('products.code')}
              value={product?.code || ''}
              InputProps={{
                readOnly: true,
              }}
              sx={{
                '& .MuiInputBase-root': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            />
          </Box>
          <Box sx={{ flex: { xs: '1', sm: '0.25' } }}>
            <TextField
              fullWidth
              label={translate('products.item_type')}
              value={product ? getTypeLabel(product.type) : ''}
              InputProps={{
                readOnly: true,
              }}
              sx={{
                '& .MuiInputBase-root': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            />
          </Box>
          <Box sx={{ flex: { xs: '1', sm: '0.25' } }}>
            <TextField
              fullWidth
              label={translate('products.status')}
              value={product ? getStatusLabel(product.status) : ''}
              InputProps={{
                readOnly: true,
              }}
              sx={{
                '& .MuiInputBase-root': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            />
          </Box>
        </Box>
        <Box>
          <TextField
            fullWidth
            label={translate('products.description')}
            value={product?.description || ''}
            InputProps={{
              readOnly: true,
            }}
            multiline
            rows={4}
            sx={{
              '& .MuiInputBase-root': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          />
        </Box>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" component="h2" gutterBottom>
          {translate('products.image_gallery')}
        </Typography>
        <MediaGallery
          metadataKey="product_image"
          metadataValue={product?.id || id}
          accept="image/*"
          readOnly={true}
        />
      </Box>

      <Typography variant="h5" component="h2" gutterBottom>
        SKUs
      </Typography>

      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{translate('products.code')}</TableCell>
              <TableCell>Preço</TableCell>
              <TableCell>Estoque</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Atributos</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  {translate('common.loading')}
                </TableCell>
              </TableRow>
            ) : skus.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  Nenhum SKU encontrado
                </TableCell>
              </TableRow>
            ) : (
              skus.map((sku) => (
                <TableRow key={sku.id}>
                  <TableCell>{sku.code}</TableCell>
                  <TableCell>{formatPrice(sku.price, sku.price_type || 'FIXED')}</TableCell>
                  <TableCell>{sku.stock}</TableCell>
                  <TableCell>
                    <Chip
                      label={sku.status === 'ACTIVE' ? translate('products.active') : translate('products.inactive')}
                      color={getStatusColor(sku.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {formatAttributes(sku.attributes)}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProductViewPage;
